<template>
  <div
    class="column scoped flex-1"
    :class="{ 'is-mobile': $isMobile }"
    :style="{
      height: $isMobile ? 'calc(100vh - 70px)' : '',
      minHeight: 0,
    }"
  >
    <!-- This wrapper div is useless, but if we directly put a QSpinner,
    there is a graphic bug on iOS -->
    <div
      v-if="loading"
      class="row justify-center"
    >
      <QSpinner
        class="q-mt-lg"
        :size="30"
      />
    </div>
    <div
      v-else
      class="column col no-wrap full-width bg-white"
    >
      <div
        class="row q-pa-smd no-wrap items-center justify-start"
        style="flex-shrink: 0; align-items: flex-start"
      >
        <img
          :alt="$t('tv_preview.source_logo')"
          class="logo"
          :src="tvMention.source.logo_url"
        />
        <div class="column">
          <span class="alternate q-mt-xs">
            <strong class="bigger2 source-name">
              {{ tvMention.source.name }}
            </strong>

            {{ tvMention.source.location }}
          </span>
          <span class="bold">
            <QIcon name="ion-md-time" />
            {{
              formatIntlDate(tvMention.start_time, {
                day: "numeric",
                month: "short",
                hour: "numeric",
                minute: "numeric",
                timeZone: tvMention.source.time_zone,
              })
            }}

            ({{ tvMention.source.time_zone }})
          </span>
        </div>
      </div>
      <InlinePlayer
        v-if="clips.length"
        auto-fetch-neighbours
        class="q-mr-sm"
        :clip="clips[0]"
        :mention="tvMention"
        :origin="origin"
        :poster="thumbnail"
        :request-media-item-button="canRequestMediaItem"
        :start-time="videoStartTime"
        type="video"
      />
      <div
        class="q-px-smd scroll flex-1 bg-white"
        :class="{ scroll: $isMobile }"
      >
        <p
          v-if="showOnScreenKeywords(tvType)"
          class="q-px-xs q-pt-md"
        >
          <template v-if="tvType === 'tv_logo_appearance'">
            {{ $t("tv_preview.logo_on_screen") }}
          </template>

          <template v-else>
            {{ $t("tv_preview.text_on_screen") }}
          </template>
          <!-- eslint-disable vue/no-v-html -->
          <span v-html="highlightKeywords" />
        </p>
        <div
          v-if="datahubContent"
          class="q-mt-md soft full-width"
        >
          <div class="column col text-justify">
            <span class="q-mb-smd highlightable self-start">
              {{ tvMention.text }}
            </span>
          </div>
        </div>
        <TVMentionTranscript
          v-else
          :caption-fragments="fragments"
          :keywords="keywords"
          :mention="tvMention"
          :thumbnails="thumbnails"
        />
      </div>
    </div>

    <ActionSheet
      v-if="$isMobile"
      ref="actionSheetMention"
      :mention="tvMention"
      on-nav-bar
    />
  </div>
</template>

<script>
import InlinePlayer from "shared/components/players/InlinePlayer.vue";
import TVMentionTranscript from "shared/components/TVMentionTranscript.vue";
import { formatIntlDate } from "shared/helpers/date";
import { constantize } from "shared/helpers/string";

const OFFSET = 5;

export default {
  name: "TvPreview",
  components: {
    InlinePlayer,
    TVMentionTranscript,
  },
  props: {
    tvType: {
      type: String,
      required: true,
    },
    id: {
      type: [Number, String],
      required: true,
    },
    keywords: {
      type: Array,
      default: () => [],
    },
    onScreenKeywords: {
      type: Array,
      default: () => [],
    },
    origin: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: true,
      tvMention: {},
      clips: [],
      active: false,
      fragments: [],
    };
  },
  computed: {
    datahubContent() {
      return this.tvMention.datahub;
    },
    canRequestMediaItem() {
      return (
        this.$features.has("media_request") &&
        this.$permissions.has("can_request_media_items")
      );
    },
    highlightKeywords() {
      let keywords = this.keywords || this.$route.query.keywords || [];
      if (this.onScreenKeywords.length) keywords = this.onScreenKeywords;

      return keywords
        .filter((keyword) => keyword)
        .map((keyword) => `<em>${keyword}</em>`)
        .join(", ");
    },
    transcriptParams() {
      return {
        after: this.firstMentionTime - 30,
        before: this.firstMentionTime + 60,
        limit: 100,
      };
    },
    firstMentionTime() {
      const index = this.tvMention.fragments.findIndex(
        (fragment) => fragment.text.search(this.keywords[0]) !== -1
      );

      return index !== -1
        ? this.tvMention.fragments[index].timestamp
        : this.tvMention.start_time;
    },
    videoStartTime() {
      if (this.tvType === "tv_logo_appearance") {
        return (this.tvMention.start_time - 1) * 1000;
      }

      return (this.firstMentionTime - OFFSET) * 1000;
    },
    thumbnail() {
      return this.thumbnails && this.thumbnails.length
        ? this.thumbnails[0].url
        : null;
    },
  },
  async mounted() {
    if (this.$isMobile)
      this.$navBar.buttonClicked(
        "show-action-sheet-caption",
        this.showActionSheet
      );

    this.tvMention = (await this[`load${constantize(this.tvType)}`]()).data;
    this.tvMention.type = this.tvType;
    this.tvMention.fragments = this.tvMention.fragments || [];

    if (this.datahubContent) {
      this.loadDatahubClip();
    } else {
      await this.loadMedia();
    }

    this.loading = false;
  },
  activated() {
    this.active = true;
  },
  deactivated() {
    this.active = false;
  },
  methods: {
    formatIntlDate,
    loadDatahubClip() {
      if (this.tvMention.datahub_media_url) {
        this.clips = [
          {
            start_time: this.tvMention.start_time,
            end_time: this.tvMention.end_time,
            media_url: this.tvMention.datahub_media_url,
          },
        ];
      }
    },
    async loadMedia() {
      this.fragments = (await this.loadFragments(this.transcriptParams)).data;
      this.thumbnails = (await this.loadThumbnails(this.transcriptParams)).data;
      const clips = (await this.loadClips()).data;
      this.clips = [];

      if (clips.length) {
        const clipContainingFirstMention = clips.filter(
          (clip) =>
            clip.start_time <= this.firstMentionTime &&
            clip.end_time >= this.firstMentionTime
        );

        this.clips.push(clipContainingFirstMention[0] || clips[0]);

        this.clips = this.clips.map((clip) => ({
          ...clip,
          program_airing: this.tvMention.program_airing,
          source: this.tvMention.source,
        }));
      }
    },
    loadTvCaption() {
      return this.$streemApiV1.get(`tv_captions/${this.id}`);
    },
    loadTvSuper() {
      return this.$streemApiV1.get(`tv_supers/${this.id}`);
    },
    loadTvLogoAppearance() {
      return this.$streemApiV1.get(`tv_logo_appearances/${this.id}`);
    },
    loadClips() {
      // fetching the 30-s clip starting between 30 seconds excluded before first
      // mention  until first mention time included
      return this.$streemApiV1.get(
        `tv_channels/${this.tvMention.source.id}/clips`,
        {
          params: {
            after: Math.min(
              this.firstMentionTime - 30,
              this.tvMention.start_time
            ),
            before: this.firstMentionTime + 15,
            limit: 3,
          },
        }
      );
    },
    loadFragments(transcriptParams) {
      let type;

      if (this.tvType === "tv_super" || this.tvType === "tv_logo_appearance") {
        type = "tv_caption";
      } else {
        type = this.tvType;
      }

      return this.$streemApiV1.get(
        `tv_channels/${this.tvMention.source.id}/${type}_fragments`,
        {
          params: transcriptParams,
        }
      );
    },
    loadThumbnails(transcriptParams) {
      return this.$streemApiV1.get(
        `tv_channels/${this.tvMention.source.id}/thumbnails`,
        {
          params: transcriptParams,
        }
      );
    },
    showActionSheet() {
      if (!this.active) {
        return;
      }

      this.$refs.actionSheetMention.show();
    },
    updateStartTime(startTime) {
      const diff = startTime - this.clips[0].start_time;

      return diff >= 0 ? diff : 0;
    },
    showOnScreenKeywords(tvType) {
      if (tvType === "tv_caption") {
        return this.onScreenKeywords.length;
      }

      return (
        ["tv_super", "tv_logo_appearance"].includes(tvType) &&
        (this.$route.query.keywords || this.keywords)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.flex-1 {
  flex: 1;
}

.logo {
  width: 40px;
  margin-right: 15px;
}

.q-icon {
  vertical-align: 0;
}

.scoped:deep(em) {
  background-color: rgb(252 220 89 / 30%);
  padding: 1px 2px;
  font-style: normal;
}

.source-name {
  width: auto;
}

// Put player in fullscreen when orientation is landscape
@media screen and (orientation: landscape) {
  .is-mobile {
    :deep(.player) {
      position: fixed;
      width: 100%;
      height: calc(100% - 39px) !important;
      top: 0;
      left: 0;
      z-index: 10000;
      background-color: black;

      video {
        margin: auto;
        width: auto;
        height: 100%;
      }
    }
  }
}
</style>
